import { arrayToString } from "./arrays"

/**
 * Formats modifier aliases for display as a comma-separated string.
 * Only returns a formatted string if aliases exist and are active.
 *
 * @param modifier - Modifier object containing aliases and activation state
 * @param modifier.aliases - Array of alias objects with name properties
 * @param modifier.aliasActive - Whether aliases should be displayed
 * @returns Formatted alias string or null if no valid aliases to display
 *
 * @example
 * aliasesFormatted({ aliases: [{ name: "Rapid Fire" }], aliasActive: true })
 * // "Rapid Fire"
 *
 * aliasesFormatted({ aliases: [{ name: "Anti-Infantry" }, { name: "Lethal Hits" }], aliasActive: true })
 * // "Anti-Infantry, Lethal Hits"
 *
 * aliasesFormatted({ aliases: [], aliasActive: true })
 * // null
 */
function aliasesFormatted(modifier: {
  aliases?: Array<{ name: string }>
  aliasActive?: boolean
}): string | null {
  if (typeof modifier.aliases === "undefined") {
    return null
  }
  if (modifier.aliases.length === 0 && modifier.aliasActive) {
    return null
  }
  if (!modifier.aliasActive) {
    return null
  }
  const aliasNames = modifier.aliases.map((alias) => alias.name)
  return `${arrayToString(aliasNames)}`
}

/**
 * Removes all whitespace characters from a string including spaces, tabs, newlines, and carriage returns.
 * Useful for normalizing input strings or creating compact identifiers.
 *
 * @param input - String to process
 * @returns String with all whitespace removed
 *
 * @example
 * removeAllWhitespace("hello world") // "helloworld"
 * removeAllWhitespace("2D6 + 3") // "2D6+3"
 * removeAllWhitespace("  spaced  out  ") // "spacedout"
 */
function removeAllWhitespace(input: string): string {
  return input.replaceAll(/\s/g, "")
}

/**
 * Converts input to a valid HTML class name, specifically tuned for weapon and modifier names.
 * Handles special characters, ensures valid CSS class name format, and provides fallbacks.
 *
 * Transformations applied:
 * - Converts to lowercase
 * - Replaces colons, equals, spaces with underscores
 * - Consolidates multiple delimiters
 * - Converts special symbols (* → asterisk, + → plus, ! → not_)
 * - Removes invalid CSS class characters
 * - Prefixes with "n-" if starting with digit
 * - Prefixes with "n" if starting with hyphen + digit
 * - Returns "foobar" if result would be empty
 *
 * @param input - Input string to sanitize (can be null/undefined)
 * @returns Valid HTML class name string (never empty)
 *
 * @example
 * sanitiseClassName("Bolt Rifle: Rapid Fire 1") // "bolt_rifle_rapid_fire_1"
 * sanitiseClassName("Power Sword +1") // "power_sword_plus1"
 * sanitiseClassName("123invalid") // "n-123invalid"
 * sanitiseClassName(null) // ""
 */
function sanitiseClassName(input: string | null | undefined): string {
  if (input === null || input === undefined) {
    return ""
  }

  let className = input.toLowerCase()

  const delimiter = "_"
  const charsToReplaceWithDelimiter = ":= ".split("")
  charsToReplaceWithDelimiter.forEach((char) => {
    className = className.replaceAll(char, delimiter)
  })

  className = className.replaceAll(`${delimiter}${delimiter}`, delimiter)
  className = className.replaceAll(`${delimiter}${delimiter}`, delimiter)
  className = className.replaceAll(`${delimiter}-${delimiter}`, "-")
  className = className.replaceAll("*", "asterisk")
  className = className.replaceAll("+", "plus")
  className = className.replaceAll("!", `not${delimiter}`)

  // Remove anything that isn't a valid class name character.
  // Keeps a-z, A-Z, 0-9, underscores & hyphens.
  const regex = /[^a-zA-Z0-9_-]/g
  className = className.replaceAll(regex, "")
  // className cannot start with a digit.
  if (!isNaN(Number(className.substr(0, 1)))) {
    className = `n-${className}`
  }
  // className cannot start with a hyphen followed by a digit.
  if (className.substr(0, 1) === "-") {
    if (!isNaN(Number(className.substr(1, 1)))) {
      className = `n${className}`
    }
  }
  // className must contain at least 1 character or be meaningful.
  if (className === "" || className === "n-") {
    className = "foobar"
  }
  return className
}

export { aliasesFormatted, removeAllWhitespace, sanitiseClassName }
